import { useMutation, useQuery } from '@tanstack/react-query';
import type { GrammarCheckResult, TranslateResult } from '@peles/shared';
import { checkGrammar, fetchAiFeatures, fetchBedrockModels, translateDraft } from '../api/ai';

/**
 * The AI flows this application has.
 *
 * Long stale time: the registry is a code fact, so it changes on deploy and
 * never between two renders of a settings page.
 */
export function useAiFeatures(enabled = true) {
  return useQuery({
    queryKey: ['ai-features'],
    queryFn: fetchAiFeatures,
    enabled,
    staleTime: 60 * 60 * 1000,
  });
}

/** Bedrock foundation models for the App Settings model picker. */
export function useBedrockModels(enabled = true) {
  return useQuery({
    queryKey: ['bedrock-models'],
    queryFn: fetchBedrockModels,
    enabled,
    staleTime: 15 * 60 * 1000,
  });
}

/** Grammar/spelling suggestion for a draft message, in an optional tone. */
export function useGrammarCheck() {
  return useMutation<GrammarCheckResult, Error, { text: string; tone?: string }>({
    mutationFn: ({ text, tone }) => checkGrammar(text, tone),
  });
}

/** Translation of a draft message. */
export function useTranslateDraft() {
  return useMutation<
    TranslateResult,
    Error,
    { text: string; targetLanguage: string; tone?: string }
  >({
    mutationFn: ({ text, targetLanguage, tone }) => translateDraft(text, targetLanguage, tone),
  });
}
